// src/components/pages/templates/TemplateEditorPage.tsx

import React, { useState, useContext, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  Alert,
  Modal,
} from "react-native";
import SessionContext from "../../../../contexts/session";
import {
  getSoapTemplate,
  createOrUpdateSoapTemplate,
  GetTemplateResponseSchema,
  AdjustSoapTemplateRequestSchema,
} from "../../../../services/api-documentation";

interface FieldDefinition {
  type: string;
  description: string;
  required: boolean;
  options?: string[] | null;
  validation_pattern?: string | null;
  default_value?: any;
  integration_source?: {
    integration_type: string;
    template_id: number;
    field_id: number;
    original_name: string;
  } | null;
}

interface SOAPSection {
  section_name: string;
  fields: { [key: string]: FieldDefinition };
  extraction_prompt?: string | null;
}

interface TemplateData {
  clinic_id: string;
  clinic_name: string;
  subjective: SOAPSection;
  objective: SOAPSection;
  assessment: SOAPSection;
  plan: SOAPSection;
  output_mappings: any;
  processing_settings: any;
}

const SECTION_NAMES = {
  subjective: "Subjective",
  objective: "Objective",
  assessment: "Assessment",
  plan: "Plan",
} as const;

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "boolean", label: "Boolean" },
  { value: "dropdown", label: "Dropdown" },
  { value: "number", label: "Number" },
] as const;

const TemplateEditorPage = () => {
  const [template, setTemplate] = useState<GetTemplateResponseSchema | null>(
    null,
  );
  const [templateData, setTemplateData] = useState<TemplateData | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] =
    useState<keyof typeof SECTION_NAMES>("subjective");
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [editingField, setEditingField] = useState<{
    section: string;
    fieldName: string;
    field: FieldDefinition;
  } | null>(null);

  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;

  const loadTemplate = async () => {
    if (!session) return;

    setLoading(true);
    try {
      const templateResponse = await getSoapTemplate(session);
      setTemplate(templateResponse);
      setTemplateData(templateResponse.template_data as TemplateData);
    } catch (error) {
      console.error("Failed to load template:", error);
      Alert.alert("Error", "Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!session || !templateData) return;

    setLoading(true);
    try {
      const request: AdjustSoapTemplateRequestSchema = {
        template_data: templateData,
        integration_type: undefined, // Let backend determine
      };

      const response = await createOrUpdateSoapTemplate(session, request);
      if (response.template_data && response.template_source) {
        setTemplate({
          template_data: response.template_data,
          template_source: response.template_source,
        });
        setTemplateData(response.template_data as TemplateData);
      }
      Alert.alert("Success", "Template updated successfully");
    } catch (error) {
      console.error("Failed to save template:", error);
      Alert.alert("Error", "Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  const addField = (
    section: keyof typeof SECTION_NAMES,
    fieldName: string,
    fieldDef: FieldDefinition,
  ) => {
    if (!templateData) return;

    // Check if field already exists
    if (templateData[section].fields[fieldName]) {
      Alert.alert("Error", "Field with this name already exists");
      return;
    }

    const updatedData = { ...templateData };
    updatedData[section] = {
      ...updatedData[section],
      fields: {
        ...updatedData[section].fields,
        [fieldName]: fieldDef,
      },
    };
    setTemplateData(updatedData);
  };

  const updateField = (
    section: keyof typeof SECTION_NAMES,
    fieldName: string,
    fieldDef: FieldDefinition,
  ) => {
    if (!templateData) return;

    const updatedData = { ...templateData };
    updatedData[section] = {
      ...updatedData[section],
      fields: {
        ...updatedData[section].fields,
        [fieldName]: fieldDef,
      },
    };
    setTemplateData(updatedData);
  };

  const removeField = (
    section: keyof typeof SECTION_NAMES,
    fieldName: string,
  ) => {
    if (!templateData) return;

    Alert.alert(
      "Confirm Delete",
      `Are you sure you want to remove the field "${fieldName}"?`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: () => {
            const updatedData = { ...templateData };
            const newFields = { ...updatedData[section].fields };
            delete newFields[fieldName];
            updatedData[section] = {
              ...updatedData[section],
              fields: newFields,
            };
            setTemplateData(updatedData);
          },
        },
      ],
    );
  };

  const moveField = (
    fromSection: keyof typeof SECTION_NAMES,
    toSection: keyof typeof SECTION_NAMES,
    fieldName: string,
  ) => {
    if (!templateData || fromSection === toSection) return;

    const fieldDef = templateData[fromSection].fields[fieldName];
    if (!fieldDef) return;

    // Check if field already exists in target section
    if (templateData[toSection].fields[fieldName]) {
      Alert.alert(
        "Error",
        `Field "${fieldName}" already exists in ${SECTION_NAMES[toSection]} section`,
      );
      return;
    }

    const updatedData = { ...templateData };

    // Remove from source section
    const fromFields = { ...updatedData[fromSection].fields };
    delete fromFields[fieldName];
    updatedData[fromSection] = {
      ...updatedData[fromSection],
      fields: fromFields,
    };

    // Add to target section
    updatedData[toSection] = {
      ...updatedData[toSection],
      fields: {
        ...updatedData[toSection].fields,
        [fieldName]: fieldDef,
      },
    };

    setTemplateData(updatedData);
    Alert.alert(
      "Success",
      `Moved "${fieldName}" to ${SECTION_NAMES[toSection]} section`,
    );
  };

  useEffect(() => {
    loadTemplate();
  }, [session]);

  if (!session) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Please log in to edit templates</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>✏️ Template Editor</Text>
        <Text style={styles.subtitle}>
          Edit SOAP template fields and structure
        </Text>

        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={styles.refreshButton}
            onPress={loadTemplate}
            disabled={loading}
          >
            <Text style={styles.buttonText}>
              {loading ? "Loading..." : "🔄 Refresh"}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.saveButton}
            onPress={saveTemplate}
            disabled={loading || !templateData}
          >
            <Text style={styles.buttonText}>
              {loading ? "Saving..." : "💾 Save Changes"}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {template && (
        <View style={styles.templateInfo}>
          <Text style={styles.infoText}>
            <Text style={styles.infoLabel}>Template Source:</Text>{" "}
            {template.template_source}
          </Text>
          <Text style={styles.infoText}>
            <Text style={styles.infoLabel}>Clinic:</Text>{" "}
            {templateData?.clinic_name}
          </Text>
        </View>
      )}

      {/* Section Tabs */}
      <View style={styles.sectionTabs}>
        {Object.entries(SECTION_NAMES).map(([key, label]) => (
          <TouchableOpacity
            key={key}
            style={[
              styles.sectionTab,
              selectedSection === key && styles.activeTab,
            ]}
            onPress={() =>
              setSelectedSection(key as keyof typeof SECTION_NAMES)
            }
          >
            <Text
              style={[
                styles.sectionTabText,
                selectedSection === key && styles.activeTabText,
              ]}
            >
              {label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Field Editor */}
      {templateData && (
        <View style={styles.fieldEditor}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>
              {SECTION_NAMES[selectedSection]} Fields
            </Text>
            <TouchableOpacity
              style={styles.addFieldButton}
              onPress={() => setShowAddFieldModal(true)}
            >
              <Text style={styles.addFieldButtonText}>+ Add Field</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.fieldsList}>
            {Object.entries(templateData[selectedSection].fields).map(
              ([fieldName, fieldDef]) => (
                <FieldRow
                  key={fieldName}
                  fieldName={fieldName}
                  fieldDef={fieldDef}
                  section={selectedSection}
                  onEdit={() =>
                    setEditingField({
                      section: selectedSection,
                      fieldName,
                      field: fieldDef,
                    })
                  }
                  onRemove={() => removeField(selectedSection, fieldName)}
                  onMove={(toSection) =>
                    moveField(
                      selectedSection,
                      toSection as keyof typeof SECTION_NAMES,
                      fieldName,
                    )
                  }
                />
              ),
            )}

            {Object.keys(templateData[selectedSection].fields).length === 0 && (
              <Text style={styles.emptyMessage}>
                No fields in this section. Add a field to get started.
              </Text>
            )}
          </View>
        </View>
      )}

      {/* Add Field Modal */}
      <AddFieldModal
        visible={showAddFieldModal}
        section={selectedSection}
        onClose={() => setShowAddFieldModal(false)}
        onAdd={(fieldName, fieldDef) => {
          addField(selectedSection, fieldName, fieldDef);
          setShowAddFieldModal(false);
        }}
      />

      {/* Edit Field Modal */}
      <EditFieldModal
        visible={!!editingField}
        editingField={editingField}
        onClose={() => setEditingField(null)}
        onUpdate={(fieldDef) => {
          if (editingField) {
            updateField(
              editingField.section as keyof typeof SECTION_NAMES,
              editingField.fieldName,
              fieldDef,
            );
          }
          setEditingField(null);
        }}
      />
    </ScrollView>
  );
};

// Field Row Component
const FieldRow = ({
  fieldName,
  fieldDef,
  section,
  onEdit,
  onRemove,
  onMove,
}: {
  fieldName: string;
  fieldDef: FieldDefinition;
  section: string;
  onEdit: () => void;
  onRemove: () => void;
  onMove: (toSection: string) => void;
}) => {
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  return (
    <View style={styles.fieldRow}>
      <View style={styles.fieldInfo}>
        <Text style={styles.fieldName}>{fieldName}</Text>
        <Text style={styles.fieldType}>{fieldDef.type}</Text>
        <Text style={styles.fieldDescription} numberOfLines={2}>
          {fieldDef.description}
        </Text>
        {fieldDef.integration_source && (
          <Text style={styles.integrationBadge}>
            🔗 {fieldDef.integration_source.integration_type}
          </Text>
        )}
      </View>

      <View style={styles.fieldActions}>
        <TouchableOpacity style={styles.actionButton} onPress={onEdit}>
          <Text style={styles.actionButtonText}>✏️</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => setShowMoveMenu(true)}
        >
          <Text style={styles.actionButtonText}>↔️</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.actionButton, styles.deleteButton]}
          onPress={onRemove}
        >
          <Text style={styles.actionButtonText}>🗑️</Text>
        </TouchableOpacity>
      </View>

      {/* Move Menu Modal */}
      <Modal visible={showMoveMenu} transparent animationType="fade">
        <TouchableOpacity
          style={styles.modalOverlay}
          onPress={() => setShowMoveMenu(false)}
        >
          <View style={styles.moveMenu}>
            <Text style={styles.moveMenuTitle}>Move to section:</Text>
            {Object.entries(SECTION_NAMES).map(
              ([key, label]) =>
                key !== section && (
                  <TouchableOpacity
                    key={key}
                    style={styles.moveOption}
                    onPress={() => {
                      onMove(key);
                      setShowMoveMenu(false);
                    }}
                  >
                    <Text style={styles.moveOptionText}>{label}</Text>
                  </TouchableOpacity>
                ),
            )}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

// Add Field Modal Component
const AddFieldModal = ({
  visible,
  section,
  onClose,
  onAdd,
}: {
  visible: boolean;
  section: string;
  onClose: () => void;
  onAdd: (fieldName: string, fieldDef: FieldDefinition) => void;
}) => {
  const [fieldName, setFieldName] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [description, setDescription] = useState("");
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState("");

  const resetForm = () => {
    setFieldName("");
    setFieldType("text");
    setDescription("");
    setRequired(false);
    setOptions("");
  };

  const handleAdd = () => {
    if (!fieldName.trim() || !description.trim()) {
      Alert.alert("Error", "Field name and description are required");
      return;
    }

    const fieldDef: FieldDefinition = {
      type: fieldType,
      description: description.trim(),
      required,
      options:
        fieldType === "dropdown"
          ? options
              .split("\n")
              .map((opt) => opt.trim())
              .filter((opt) => opt)
          : null,
      validation_pattern: null,
      default_value: null,
      integration_source: null,
    };

    onAdd(fieldName.trim().toLowerCase().replace(/\s+/g, "_"), fieldDef);
    resetForm();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>
            Add New Field to{" "}
            {SECTION_NAMES[section as keyof typeof SECTION_NAMES]}
          </Text>
          <TouchableOpacity onPress={onClose}>
            <Text style={styles.closeButton}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalContent}>
          <View style={styles.formGroup}>
            <Text style={styles.label}>Field Name:</Text>
            <TextInput
              style={styles.input}
              value={fieldName}
              onChangeText={setFieldName}
              placeholder="e.g., chief_complaint"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Field Type:</Text>
            <View style={styles.typeSelector}>
              {FIELD_TYPES.map((type) => (
                <TouchableOpacity
                  key={type.value}
                  style={[
                    styles.typeOption,
                    fieldType === type.value && styles.selectedType,
                  ]}
                  onPress={() => setFieldType(type.value)}
                >
                  <Text
                    style={[
                      styles.typeOptionText,
                      fieldType === type.value && styles.selectedTypeText,
                    ]}
                  >
                    {type.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Description:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={description}
              onChangeText={setDescription}
              placeholder="Describe what this field captures..."
              multiline
              numberOfLines={3}
            />
          </View>

          {fieldType === "dropdown" && (
            <View style={styles.formGroup}>
              <Text style={styles.label}>Options (one per line):</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={options}
                onChangeText={setOptions}
                placeholder="Option 1&#10;Option 2&#10;Option 3"
                multiline
                numberOfLines={4}
              />
            </View>
          )}

          <View style={styles.formGroup}>
            <TouchableOpacity
              style={styles.checkboxRow}
              onPress={() => setRequired(!required)}
            >
              <View style={[styles.checkbox, required && styles.checkedBox]}>
                {required && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={styles.checkboxLabel}>Required Field</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>

        <View style={styles.modalFooter}>
          <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.addButton} onPress={handleAdd}>
            <Text style={styles.addButtonText}>Add Field</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

// Edit Field Modal Component (similar to AddFieldModal but pre-populated)
const EditFieldModal = ({
  visible,
  editingField,
  onClose,
  onUpdate,
}: {
  visible: boolean;
  editingField: {
    section: string;
    fieldName: string;
    field: FieldDefinition;
  } | null;
  onClose: () => void;
  onUpdate: (fieldDef: FieldDefinition) => void;
}) => {
  const [fieldType, setFieldType] = useState("text");
  const [description, setDescription] = useState("");
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState("");

  useEffect(() => {
    if (editingField) {
      setFieldType(editingField.field.type);
      setDescription(editingField.field.description);
      setRequired(editingField.field.required);
      setOptions(editingField.field.options?.join("\n") || "");
    }
  }, [editingField]);

  const handleUpdate = () => {
    if (!description.trim()) {
      Alert.alert("Error", "Description is required");
      return;
    }

    const fieldDef: FieldDefinition = {
      type: fieldType,
      description: description.trim(),
      required,
      options:
        fieldType === "dropdown"
          ? options
              .split("\n")
              .map((opt) => opt.trim())
              .filter((opt) => opt)
          : null,
      validation_pattern: editingField?.field.validation_pattern || null,
      default_value: editingField?.field.default_value || null,
      integration_source: editingField?.field.integration_source || null,
    };

    onUpdate(fieldDef);
  };

  if (!editingField) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>
            Edit Field: {editingField.fieldName}
          </Text>
          <TouchableOpacity onPress={onClose}>
            <Text style={styles.closeButton}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalContent}>
          {editingField.field.integration_source && (
            <View style={styles.warningBox}>
              <Text style={styles.warningText}>
                ⚠️ This field is linked to{" "}
                {editingField.field.integration_source.integration_type}.
                Changes may affect integration functionality.
              </Text>
            </View>
          )}

          <View style={styles.formGroup}>
            <Text style={styles.label}>Field Type:</Text>
            <View style={styles.typeSelector}>
              {FIELD_TYPES.map((type) => (
                <TouchableOpacity
                  key={type.value}
                  style={[
                    styles.typeOption,
                    fieldType === type.value && styles.selectedType,
                  ]}
                  onPress={() => setFieldType(type.value)}
                >
                  <Text
                    style={[
                      styles.typeOptionText,
                      fieldType === type.value && styles.selectedTypeText,
                    ]}
                  >
                    {type.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Description:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={description}
              onChangeText={setDescription}
              placeholder="Describe what this field captures..."
              multiline
              numberOfLines={3}
            />
          </View>

          {fieldType === "dropdown" && (
            <View style={styles.formGroup}>
              <Text style={styles.label}>Options (one per line):</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={options}
                onChangeText={setOptions}
                placeholder="Option 1&#10;Option 2&#10;Option 3"
                multiline
                numberOfLines={4}
              />
            </View>
          )}

          <View style={styles.formGroup}>
            <TouchableOpacity
              style={styles.checkboxRow}
              onPress={() => setRequired(!required)}
            >
              <View style={[styles.checkbox, required && styles.checkedBox]}>
                {required && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={styles.checkboxLabel}>Required Field</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>

        <View style={styles.modalFooter}>
          <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.updateButton} onPress={handleUpdate}>
            <Text style={styles.updateButtonText}>Update Field</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f8f9fa",
  },
  header: {
    padding: 20,
    backgroundColor: "white",
    borderBottomWidth: 1,
    borderBottomColor: "#dee2e6",
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#212529",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: "#6c757d",
    marginBottom: 16,
  },
  actionButtons: {
    flexDirection: "row",
    gap: 12,
  },
  refreshButton: {
    backgroundColor: "#17a2b8",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    flex: 1,
  },
  saveButton: {
    backgroundColor: "#28a745",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    flex: 1,
  },
  buttonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "600",
    textAlign: "center",
  },
  errorText: {
    textAlign: "center",
    color: "#dc3545",
    fontSize: 16,
    marginTop: 50,
  },
  templateInfo: {
    backgroundColor: "white",
    padding: 16,
    marginHorizontal: 20,
    marginTop: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  infoText: {
    fontSize: 14,
    color: "#495057",
    marginBottom: 4,
  },
  infoLabel: {
    fontWeight: "600",
  },
  sectionTabs: {
    flexDirection: "row",
    backgroundColor: "white",
    marginHorizontal: 20,
    marginTop: 16,
    borderRadius: 8,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  sectionTab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    backgroundColor: "#f8f9fa",
  },
  activeTab: {
    backgroundColor: "#007bff",
  },
  sectionTabText: {
    textAlign: "center",
    fontSize: 14,
    fontWeight: "600",
    color: "#495057",
  },
  activeTabText: {
    color: "white",
  },
  fieldEditor: {
    backgroundColor: "white",
    margin: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#dee2e6",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#495057",
  },
  addFieldButton: {
    backgroundColor: "#28a745",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  addFieldButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "600",
  },
  fieldsList: {
    padding: 16,
  },
  emptyMessage: {
    textAlign: "center",
    color: "#6c757d",
    fontStyle: "italic",
    padding: 20,
  },
  fieldRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: "#f8f9fa",
    borderRadius: 6,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  fieldInfo: {
    flex: 1,
    marginRight: 12,
  },
  fieldName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#212529",
    marginBottom: 2,
  },
  fieldType: {
    fontSize: 12,
    color: "#007bff",
    fontWeight: "500",
    marginBottom: 4,
  },
  fieldDescription: {
    fontSize: 13,
    color: "#6c757d",
    lineHeight: 18,
  },
  integrationBadge: {
    fontSize: 11,
    color: "#ffc107",
    fontWeight: "500",
    marginTop: 4,
  },
  fieldActions: {
    flexDirection: "row",
    gap: 8,
  },
  actionButton: {
    backgroundColor: "#6c757d",
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  deleteButton: {
    backgroundColor: "#dc3545",
  },
  actionButtonText: {
    fontSize: 12,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  moveMenu: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    minWidth: 200,
  },
  moveMenuTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 12,
    textAlign: "center",
  },
  moveOption: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    marginBottom: 4,
    backgroundColor: "#f8f9fa",
  },
  moveOptionText: {
    fontSize: 14,
    textAlign: "center",
    color: "#495057",
  },
  modalContainer: {
    flex: 1,
    backgroundColor: "white",
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#dee2e6",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#212529",
    flex: 1,
  },
  closeButton: {
    fontSize: 20,
    color: "#6c757d",
    paddingLeft: 16,
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    color: "#495057",
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    backgroundColor: "white",
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: "top",
  },
  typeSelector: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  typeOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "#ced4da",
    backgroundColor: "#f8f9fa",
  },
  selectedType: {
    backgroundColor: "#007bff",
    borderColor: "#007bff",
  },
  typeOptionText: {
    fontSize: 13,
    color: "#495057",
    fontWeight: "500",
  },
  selectedTypeText: {
    color: "white",
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: "#ced4da",
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  checkedBox: {
    backgroundColor: "#007bff",
    borderColor: "#007bff",
  },
  checkmark: {
    color: "white",
    fontSize: 12,
    fontWeight: "bold",
  },
  checkboxLabel: {
    fontSize: 14,
    color: "#495057",
  },
  modalFooter: {
    flexDirection: "row",
    gap: 12,
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: "#dee2e6",
  },
  cancelButton: {
    flex: 1,
    backgroundColor: "#6c757d",
    paddingVertical: 12,
    borderRadius: 6,
  },
  cancelButtonText: {
    color: "white",
    textAlign: "center",
    fontSize: 14,
    fontWeight: "600",
  },
  addButton: {
    flex: 1,
    backgroundColor: "#28a745",
    paddingVertical: 12,
    borderRadius: 6,
  },
  addButtonText: {
    color: "white",
    textAlign: "center",
    fontSize: 14,
    fontWeight: "600",
  },
  updateButton: {
    flex: 1,
    backgroundColor: "#007bff",
    paddingVertical: 12,
    borderRadius: 6,
  },
  updateButtonText: {
    color: "white",
    textAlign: "center",
    fontSize: 14,
    fontWeight: "600",
  },
  warningBox: {
    backgroundColor: "#fff3cd",
    borderWidth: 1,
    borderColor: "#ffeaa7",
    borderRadius: 6,
    padding: 12,
    marginBottom: 16,
  },
  warningText: {
    fontSize: 13,
    color: "#856404",
    lineHeight: 18,
  },
});

export default TemplateEditorPage;
